import { Stack } from '@latitude/layout';
import { cn } from '@latitude/token-utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipProviderProps = ComponentPropsWithoutRef<typeof TooltipProvider>;

// Tailwind's class scanner needs literal class names, so line counts are
// mapped rather than interpolated (`line-clamp-${n}` would never be found).
const LINE_CLAMP_CLASSES: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

const ICON_CLASSES = [
  '[&_>svg]:w-sizing-sys-size-16',
  '[&_>svg]:h-sizing-sys-size-16',
  '[&>svg]:fill-current',
  '[&>svg]:text-current',
  'size-[16px]',
];

const TOOLTIP_CONTENT_CLASSES = [
  'z-[100]',
  'overflow-hidden',
  'bg-color-comp-tooltip-container',
  'rounded-radius-comp-action-round',
  'px-spacing-comp-action-medium-horizontal',
  'py-spacing-comp-action-medium-vertical',
  'flex',
  'items-center',
  'justify-center',
  'border-border-sys-border-2',
  'border-color-comp-tooltip-border',
  'shadow-sys-shadow-base',
  'max-w-[400px]',
  'font-comp-action-tiny-font-family',
  'font-comp-action-tiny-font-weight',
  'text-font-comp-action-tiny-font-size',
  'text-color-comp-tooltip-on-container',
  'font-comp-action-tiny-font-weight',
  'leading-font-comp-action-tiny-line-height',
  'animate-in fade-in-0 zoom-in-95',
  'data-[state=delayed-open]:data-[side=top]:slide-in-from-bottom-2',
  'data-[state=delayed-open]:data-[side=right]:slide-in-from-left-2',
  'data-[state=delayed-open]:data-[side=left]:slide-in-from-right-2',
  'data-[state=delayed-open]:data-[side=bottom]:slide-in-from-top-2',
  'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0',
  'select-none',
  'leading-none',
  'shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px]',
  'will-change-[transform,opacity]',
];

const TooltipContent = ({
  className,
  sideOffset = 4,
  side = 'top',
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof TooltipPrimitive.Content>>;
}) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    side={side}
    className={cn(TOOLTIP_CONTENT_CLASSES, className)}
    {...props}
  />
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const Tooltip = ({
  children,
  text,
  leadingElement,
  trailingElement,
  hideTooltip,
  wrapperClassName,
  clampLines = 3,
  ref,
  ...contentProps
}: ComponentPropsWithoutRef<typeof TooltipContent> & {
  leadingElement?: React.ReactNode;
  trailingElement?: React.ReactNode;
  text: React.ReactNode;
  hideTooltip?: boolean;
  wrapperClassName?: string;
  /** Max lines before truncating, or `false` to render the full text. Default: 3. */
  clampLines?: 1 | 2 | 3 | 4 | 5 | 6 | false;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  align?: TooltipPrimitive.TooltipContentProps['align'];
} & {
  ref?: Ref<ComponentRef<typeof TooltipContent>>;
}) => {
  return (
    <TooltipPrimitive.Root open={hideTooltip ? false : undefined}>
      <TooltipPrimitive.Trigger asChild>
        {/* The extra div is necessary to prevent bugs related to the child:
            When a button is disabled, it was disabling the tooltip
            When the child was an icon, the location of the tooltip was incorrect
        */}
        <Stack className={cn('w-auto', wrapperClassName)}>{children}</Stack>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipContent ref={ref} {...contentProps}>
          {leadingElement && (
            <span className={cn(ICON_CLASSES)}>{leadingElement}</span>
          )}
          <span
            className={cn(
              'mx-spacing-comp-action-medium-text-area min-w-0 whitespace-normal leading-[normal]',
              clampLines === false
                ? 'line-clamp-none'
                : LINE_CLAMP_CLASSES[clampLines],
            )}
          >
            {text}
          </span>
          {trailingElement && (
            <span className={cn(ICON_CLASSES)}>{trailingElement}</span>
          )}
        </TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

Tooltip.displayName = TooltipPrimitive.Root.displayName;

export {
  Tooltip,
  TooltipProvider,
  type TooltipProviderProps,
  TOOLTIP_CONTENT_CLASSES,
};
